import { useCallback, useEffect, useRef, useState } from 'react';
import { Eye, Loader2, Mail, MousePointerClick, RefreshCw } from 'lucide-react';
import { formatEngagementWhen } from '../../lib/emailEngagement';
import {
  loadPostmarkOutboundMessages,
  type PostmarkOutboundMessage,
} from '../../lib/postmarkOutboundMessages';

const PAGE_SIZE = 100;

function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function statusClasses(status: string | null): string {
  const s = (status ?? '').toLowerCase();
  if (s === 'sent' || s === 'delivered') {
    return 'bg-emerald-50 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200 border-emerald-200 dark:border-emerald-800';
  }
  if (s === 'queued' || s === 'processing') {
    return 'bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200 border-amber-200 dark:border-amber-800';
  }
  if (s.includes('fail') || s.includes('bounce') || s.includes('spam')) {
    return 'bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-200 border-red-200 dark:border-red-800';
  }
  return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300 border-gray-200 dark:border-gray-700';
}

function EngagementCell({ row }: { row: PostmarkOutboundMessage }) {
  if (!row.trackingEnabled) {
    return <span className="text-xs text-gray-400 dark:text-gray-500">Not tracked</span>;
  }

  return (
    <div className="space-y-1.5 min-w-[9rem]">
      <div className="flex items-start gap-1.5 text-xs">
        <Eye className="w-3.5 h-3.5 shrink-0 mt-0.5 text-sky-600 dark:text-sky-400" />
        {row.opened && row.openedAt ? (
          <span className="text-gray-700 dark:text-gray-300">
            Opened {formatEngagementWhen(row.openedAt)}
          </span>
        ) : (
          <span className="text-gray-500 dark:text-gray-400">No open</span>
        )}
      </div>
      <div className="flex items-start gap-1.5 text-xs">
        <MousePointerClick className="w-3.5 h-3.5 shrink-0 mt-0.5 text-violet-600 dark:text-violet-400" />
        {row.clickCount > 0 && row.lastClickAt ? (
          <span className="min-w-0">
            <span className="text-gray-700 dark:text-gray-300">
              {row.clickCount} click{row.clickCount === 1 ? '' : 's'} ·{' '}
              {formatEngagementWhen(row.lastClickAt)}
            </span>
            {row.lastClickUrl ? (
              <a
                href={row.lastClickUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block truncate max-w-[220px] text-emerald-600 dark:text-emerald-400 hover:underline mt-0.5"
                title={row.lastClickUrl}
              >
                {row.lastClickUrl}
              </a>
            ) : null}
          </span>
        ) : (
          <span className="text-gray-500 dark:text-gray-400">No clicks</span>
        )}
      </div>
    </div>
  );
}

export default function SubscriberPostmarkSentSection({ email }: { email: string }) {
  const [rows, setRows] = useState<PostmarkOutboundMessage[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  const load = useCallback(async (append = false) => {
    const trimmed = email.trim();
    if (!trimmed) {
      setRows([]);
      setTotalCount(0);
      setLoading(false);
      return;
    }

    if (append) setLoadingMore(true);
    else {
      setLoading(true);
      setError(null);
    }

    try {
      const offset = append ? rowsRef.current.length : 0;
      const result = await loadPostmarkOutboundMessages(trimmed, {
        count: PAGE_SIZE,
        offset,
      });
      setTotalCount(result.totalCount);
      setRows((prev) => (append ? [...prev, ...result.messages] : result.messages));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load Postmark messages');
      if (!append) {
        setRows([]);
        setTotalCount(0);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [email]);

  useEffect(() => {
    void load(false);
  }, [load]);

  const hasMore = rows.length < totalCount;

  if (!email.trim()) {
    return (
      <section className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 overflow-hidden p-8 text-center">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Add an email address to see Postmark send history.
        </p>
      </section>
    );
  }

  return (
    <section className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
            Postmark sent
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Outbound messages to {email.trim()} from this Postmark server, newest first. Opens and
            clicks come from Postmark message events.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load(false)}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          aria-label="Refresh Postmark messages"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {loading && rows.length === 0 ? (
        <div className="p-10 flex justify-center">
          <Loader2 className="w-5 h-5 text-emerald-500 animate-spin" />
        </div>
      ) : error ? (
        <div className="p-6">
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          <button
            type="button"
            onClick={() => void load(false)}
            className="mt-3 inline-flex items-center gap-1.5 text-sm text-emerald-600 dark:text-emerald-400 hover:underline"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Try again
          </button>
        </div>
      ) : rows.length === 0 ? (
        <div className="p-10 text-sm text-gray-500 dark:text-gray-400 text-center">
          <Mail className="w-8 h-8 mx-auto mb-2 opacity-40" />
          No outbound messages found for this address on Postmark.
        </div>
      ) : (
        <>
          <div className="px-6 py-2 text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700/60">
            Showing {rows.length} of {totalCount}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/40 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Sent</th>
                  <th className="text-left px-4 py-2 font-medium">Subject</th>
                  <th className="text-left px-4 py-2 font-medium">Status</th>
                  <th className="text-left px-4 py-2 font-medium">Engagement</th>
                  <th className="text-left px-4 py-2 font-medium hidden md:table-cell">Tag</th>
                  <th className="text-left px-4 py-2 font-medium hidden lg:table-cell">Stream</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
                {rows.map((r) => (
                  <tr key={r.messageId} className="hover:bg-gray-50/80 dark:hover:bg-gray-900/30">
                    <td className="px-4 py-2.5 whitespace-nowrap text-gray-700 dark:text-gray-300">
                      {formatWhen(r.receivedAt)}
                    </td>
                    <td className="px-4 py-2.5 min-w-[12rem]">
                      <span className="font-medium text-gray-900 dark:text-white line-clamp-2">
                        {r.subject || '(no subject)'}
                      </span>
                      {r.from ? (
                        <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                          From {r.from}
                        </span>
                      ) : null}
                    </td>
                    <td className="px-4 py-2.5">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-md text-xs font-medium border ${statusClasses(r.status)}`}
                      >
                        {r.status ?? '—'}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 align-top">
                      <EngagementCell row={r} />
                    </td>
                    <td className="px-4 py-2.5 hidden md:table-cell text-gray-600 dark:text-gray-400">
                      {r.tag ?? '—'}
                    </td>
                    <td className="px-4 py-2.5 hidden lg:table-cell text-gray-600 dark:text-gray-400">
                      {r.messageStream ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {hasMore ? (
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-center">
              <button
                type="button"
                onClick={() => void load(true)}
                disabled={loadingMore}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                {loadingMore ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Load more
              </button>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}
